import React, { ChangeEvent } from 'react';
import { GradientName } from '~/interfaces/GradientName';
import styles from './radio.module.scss';

type RadioSize = 'xs' | 'sm' | 'lg';

type RadioGradientDirection = 'Vertical' | 'Horizontal';

export type RadioProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  /**
   * Radio input name
   */
  name?: string;
  /**
   * Radio input is centered vertically
   */
  center?: boolean;
  /**
   * If 'true', Radio is in checked state
   */
  checked?: boolean;
  /**
   * If 'true', Radio is in disabled state
   */
  disabled?: boolean;
  /**
   * Radio border gradient name, 'multi1' by default
   */
  gradient?: GradientName;
  /**
   * Radio border gradient direction, 'Horizontal' by default
   */
  gradientDirection?: RadioGradientDirection;
  /**
   * Primary label displayed next to the Radio
   */
  primaryLabel?: string;
  /**
   * Secondary label displayed next to the Radio
   */
  secondaryLabel?: string;
  /**
   * Radio size, 'sm' by default
   */
  size?: RadioSize;
  /**
   * Callback used when the checked state changes
   */
  onChange?(event: ChangeEvent<HTMLInputElement>): void;
  /**
   * If 'true', Radio is hidden
   */
  hidden?: boolean;
  /**
   * Custom class names
   */
  customRootClassNames?: string;
  primaryLabelClassNames?: string;
};

export const Radio = (props: RadioProps) => {
  const {
    name,
    checked,
    disabled = false,
    gradient = 'multi1',
    gradientDirection = 'Horizontal',
    primaryLabel,
    secondaryLabel,
    size = 'sm',
    center = false,
    hidden = false,
    customRootClassNames,
    primaryLabelClassNames,
    onChange: onChangeProp,
    ...other
  } = props;

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeProp?.(event);
  };

  const radioContainerClasses = [
    checked && !disabled ? `${gradient}${gradientDirection}` : '',
    styles.container,
    styles[size],
    primaryLabel || secondaryLabel ? styles.hasLabel : '',
    checked ? styles.checked : '',
    disabled ? styles.disabled : '',
  ].join(' ');

  return (
    <div
      className={[
        styles.root,
        disabled ? styles.disabled : '',
        center ? styles.center : '',
        checked ? styles.checked : '',
        hidden ? styles.hidden : '',
        customRootClassNames ?? '',
      ].join(' ')}
    >
      <input
        className={styles.input}
        name={name}
        disabled={disabled}
        checked={checked}
        onChange={onChange}
        type="radio"
        {...other}
      />
      <div className={radioContainerClasses}>
        {checked ? <div className={[styles.dot, styles[size], disabled ? styles.disabled : ''].join(' ')}></div> : null}
      </div>
      <div className={styles.labelContainer}>
        <div className={[styles.primaryLabel, disabled ? styles.disabled : '', primaryLabelClassNames ?? ''].join(' ')}>
          {primaryLabel}
        </div>
        <div className={[styles.secondaryLabel, disabled ? styles.disabled : ''].join(' ')}>{secondaryLabel}</div>
      </div>
    </div>
  );
};
